import SectionHeader from "@/components/feature/panel/SectionHeader";
import EmptyState from "@/components/feature/panel/EmptyState";
import PanelLinks from "@/components/feature/panel/PanelLinks";
import ChildSelector from "@/pages/veli/components/ChildSelector";
import {
  useVeliChild,
  useChildHomework,
  HOMEWORK_STATUS_LABELS,
  HOMEWORK_STATUS_COLORS,
} from "@/hooks/useVeli";

export default function Odevler() {
  const { children, activeChild, activeUserId, setActiveUserId, loading: childrenLoading } =
    useVeliChild();
  const { data, loading, error, reload } = useChildHomework(activeChild?.userId ?? null);

  if (childrenLoading) return <EmptyState icon="ri-loader-4-line" title="Yükleniyor..." />;

  if (children.length === 0) {
    return (
      <div className="space-y-6">
        <SectionHeader
          title="Ödevler"
          description="Öğrencinin ödevlerini ve teslim durumunu takip et."
        />
        <EmptyState
          icon="ri-task-line"
          title="Henüz öğrenci hesabı bağlanmadı"
          description="Çocuğunuzun hesabını eşleştirdiğinizde ödevleri burada görünecek."
        />
        <PanelLinks
          links={[{ label: "Çocuğunu Eşleştir", href: "/veli/profil", icon: "ri-link" }]}
        />
      </div>
    );
  }

  const list = data ?? [];
  const pending = list.filter((h) => h.status !== "tamamlandi").length;
  const done = list.length - pending;

  return (
    <div className="space-y-6">
      <SectionHeader
        title="Ödevler"
        description={`${activeChild?.fullName ?? "Öğrencinin"} ödevleri ve teslim durumu.`}
      />
      <ChildSelector
        children={children}
        activeUserId={activeUserId}
        onSelect={setActiveUserId}
      />

      {loading ? (
        <EmptyState icon="ri-loader-4-line" title="Yükleniyor..." />
      ) : error ? (
        <div className="rounded-lg border border-background-200 bg-background-50 p-6 text-center">
          <p className="text-sm text-foreground-600">{error}</p>
          <button
            type="button"
            onClick={reload}
            className="mt-4 inline-flex items-center gap-2 whitespace-nowrap px-4 py-2 rounded-md text-sm font-semibold bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors"
          >
            <i className="ri-refresh-line" aria-hidden="true"></i>
            Tekrar dene
          </button>
        </div>
      ) : list.length === 0 ? (
        <EmptyState
          icon="ri-task-line"
          title="Henüz ödev yok"
          description="Ödevler oluşturulduğunda burada görünecek."
        />
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <div className="rounded-lg border border-background-200 bg-background-50 p-4">
              <p className="text-xs font-medium text-foreground-500">Toplam ödev</p>
              <p className="mt-1 font-heading font-bold text-2xl text-foreground-950">{list.length}</p>
            </div>
            <div className="rounded-lg border border-background-200 bg-background-50 p-4">
              <p className="text-xs font-medium text-foreground-500">Tamamlanan</p>
              <p className="mt-1 font-heading font-bold text-2xl text-accent-700">{done}</p>
            </div>
            <div className="rounded-lg border border-background-200 bg-background-50 p-4">
              <p className="text-xs font-medium text-foreground-500">Bekleyen</p>
              <p className="mt-1 font-heading font-bold text-2xl text-foreground-950">{pending}</p>
            </div>
          </div>

          <div className="space-y-3">
            {list.map((h) => (
              <div
                key={h.id}
                className="rounded-lg border border-background-200 bg-background-50 p-4 flex flex-col sm:flex-row sm:items-center gap-3"
              >
                <span className="w-10 h-10 flex items-center justify-center rounded-md bg-background-100 text-foreground-500 shrink-0">
                  <i className="ri-task-line text-lg" aria-hidden="true"></i>
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-foreground-950">{h.title}</p>
                  <p className="text-xs text-foreground-500 mt-0.5">
                    {h.subject}
                    {h.teacher ? ` · ${h.teacher}` : ""}
                    {h.dueDate ? ` · Teslim: ${h.dueDate}` : ""}
                  </p>
                  {h.description && (
                    <p className="text-xs text-foreground-500 mt-1 line-clamp-2">{h.description}</p>
                  )}
                </div>
                <span
                  className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${HOMEWORK_STATUS_COLORS[h.status] ?? "bg-background-100 text-foreground-600"}`}
                >
                  {HOMEWORK_STATUS_LABELS[h.status] ?? h.status}
                </span>
              </div>
            ))}
          </div>
        </>
      )}

      <PanelLinks
        links={[
          { label: "Haftalık Rapor", href: "/veli/haftalik-rapor", icon: "ri-bar-chart-line" },
          { label: "Devamsızlık", href: "/veli/devamsizlik", icon: "ri-user-unfollow-line" },
        ]}
      />
    </div>
  );
}